import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ClipboardList, Plus, ArrowRight, Download } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import OrderAgainButton from "@/components/OrderAgainButton";
import Seo from "@/components/Seo";

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/reports").then(({ data }) => setReports(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => (r.address?.full || "").toLowerCase().includes(q));
  }, [reports, query]);

  return (
    <div className="container-mtr py-8 sm:py-12" data-testid="reports-page">
      <Seo title="My Reports — MeasureTheRoof" description="All your roof measurement reports in one place." path="/reports" noindex />
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <span className="eyebrow">History</span>
          <h1 className="font-body font-extrabold text-navy text-[26px] sm:text-[32px] mt-1 flex items-center gap-2">
            <ClipboardList className="text-mortar" size={26} /> My Reports
          </h1>
        </div>
        <Link to="/dashboard" className="btn-primary !min-h-[44px]" data-testid="reports-new"><Plus size={18} /> New report</Link>
      </div>

      <div className="relative max-w-md mb-6">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
        <input
          className="input-mtr pl-12"
          placeholder="Search by address…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="reports-search"
          aria-label="Search reports by address"
        />
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="card-mtr h-20 skeleton rounded-card" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="card-mtr text-center py-12" data-testid="reports-empty">
          <div className="w-12 h-12 rounded-full bg-fieldstone grid place-items-center mx-auto mb-3"><ClipboardList className="text-orange" size={24} /></div>
          <p className="text-iron font-semibold">{query ? "No reports match your search" : "No reports yet"}</p>
          <p className="text-mortar text-[14px] mt-1">{query ? "Try a different address." : "Order your first roof report from the dashboard."}</p>
          {!query && <Link to="/dashboard" className="btn-primary mt-5 inline-flex" data-testid="reports-empty-cta">Start a report <ArrowRight size={16} /></Link>}
        </div>
      ) : (
        <div className="space-y-3" data-testid="reports-list">
          {filtered.map((r) => (
            <div key={r.report_id} className="card-mtr !p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`report-row-${r.report_id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-iron text-[15px] truncate">{r.address?.full}</p>
                <p className="text-[13px] text-mortar mt-0.5">
                  {new Date(r.created_at).toLocaleDateString()} · {r.product_name} · <span className="metric text-iron">${Number(r.customer_price).toFixed(2)}</span>
                </p>
              </div>
              <StatusBadge status={r.status} />
              <div className="flex items-center gap-2">
                <OrderAgainButton reportId={r.report_id} className="btn-ghost !min-h-[40px] !py-2 !px-4" label="Order again" testid={`report-reorder-${r.report_id}`} />
                <Link to={`/reports/${r.report_id}`} className="btn-primary !min-h-[40px] !py-2 !px-4" data-testid={`report-view-${r.report_id}`}>
                  View <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
